import { customElement, state } from "lit/decorators.js";
import { html, nothing } from "lit";
import { NotificationService } from "../../../../../libs/ui-components/notification";
import { LiveEventFormPage } from "../live-event-form-page/live-event-form-page";
import { navigateTo } from "../../router";
import { t, translate } from "../../translations/i18n";
import "../../components/share-content-modal/share-content-modal";
import type { ShareContentDetails } from "../../components/share-content-modal/share-content-modal";

@customElement("create-live-event-page")
export class CreateLiveEventPage extends LiveEventFormPage {
  @state()
  private showShareModal = false;

  @state()
  private shareContentDetails?: ShareContentDetails;
  protected override get mode() {
    return "create" as const;
  }

  protected override get urlSegment() {
    return "create";
  }

  protected override get desktopTitle() {
    const eventId = this.formState.eventId.get();
    return eventId ? t("createLiveEventPage.editLiveEvent") : t("createLiveEventPage.createTitle");
  }

  protected override get streamSettingsButtonLabel() {
    return t("createLiveEventPage.saveAndGoToEventInfo");
  }

  protected override get saveAndContinueLabel() {
    return t("createLiveEventPage.publishEvent");
  }

  protected override get isStreamSettingsTabEnabled() {
    return true;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const segmentIndex = pathSegments.indexOf(this.urlSegment);
    const eventId = segmentIndex !== -1 ? pathSegments[segmentIndex + 1] : undefined;
    if (!eventId) {
      this.formService.initializeNewEvent();
      this.activeTab = "settings";
      if (this.formState.streamSource.get() === "webcam") {
        void this.checkCameraPermission();
      }
    }
  }

  protected override handleStreamSettingsAction(): void {
    this.activeTab = "info";
  }

  protected override async handleSaveAndContinue(): Promise<void> {
    if (!this.formService.validateForm()) {
      NotificationService.error(translate("createLiveEventPage.fillRequiredFields"));
      return;
    }
    const success = await this.formService.saveEvent();
    if (success) {
      this.isSaved = true;
      this.liveEventsService.refetch();
      NotificationService.success(translate("createLiveEventPage.eventCreated"));

      // Show promo modal with created event data
      const eventId = this.formState.eventId.get();
      const eventTitle = this.formState.formData.get().title;
      const thumbnailUrl = this.formState.thumbnailUrl.get();
      if (eventId && eventTitle) {
        this.shareContentDetails = this.buildShareContentDetails(eventId, eventTitle, thumbnailUrl);
        this.showShareModal = true;
      } else {
        navigateTo("live-events");
      }
    }
  }

  private buildShareContentDetails(id: string, title: string, thumbnail_url?: string | null): ShareContentDetails {
    const isProduction = window.location.hostname.endsWith("worldofwow.com");
    const baseUrl = isProduction ? "https://worldofwow.com" : "https://worldofwow.dev";
    const handle = this.authState.currentUser.get()?.handle ?? "";
    return {
      id,
      title,
      thumbnail_url,
      shareUrl: `${baseUrl}/${handle}/live-events/${id}`,
    };
  }

  private handleShareModalClose() {
    this.showShareModal = false;
    this.shareContentDetails = undefined;
    navigateTo("live-events");
  }

  override render() {
    return html`
      ${super.render()}
      ${this.showShareModal && this.shareContentDetails
        ? html`
            <share-content-modal
              .content=${this.shareContentDetails}
              @modal-close=${this.handleShareModalClose}
            ></share-content-modal>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "create-live-event-page": CreateLiveEventPage;
  }
}
